export function compactCurrency(value: number | null, symbol: string): string {
  if (value === null) return '—';
  const magnitude = Math.abs(value);
  if (magnitude >= 1_000_000) return `${symbol}${(value / 1_000_000).toFixed(2)}M`;
  if (magnitude >= 1_000) return `${symbol}${Math.round(value / 1_000)}K`;
  return `${symbol}${value.toFixed(2)}`;
}

export function percent(value: number | null, fractionDigits = 2): string {
  return value === null ? '—' : `${(value * 100).toFixed(fractionDigits)}%`;
}

/** A creative's share of the period's total spend. */
export function spendRatio(spend: number | null, totalSpend: number | null): string {
  return spend === null || !totalSpend ? '—' : percent(spend / totalSpend, 1);
}

export function signedPercent(value: number | null): string {
  if (value === null) return '—';
  const scaled = value * 100;
  const digits = Math.abs(scaled) >= 10 ? 0 : 1;
  return `${scaled > 0 ? '+' : ''}${scaled.toFixed(digits)}%`;
}

export function count(value: number | null): string {
  return value === null ? '—' : value.toLocaleString('en-US');
}

/** 128.48M, 12.3K: large delivery counts read at a glance. */
export function compactCount(value: number | null): string {
  if (value === null) return '—';
  return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(
    value,
  );
}

export function ratio(own: number | null, benchmark: number | null): string {
  if (own === null || benchmark === null || benchmark <= 0) return '—';
  const index = own / benchmark;
  return `${index.toFixed(index < 10 ? 1 : 0)}\u00d7`;
}

export interface KpiDescriptor {
  label: string;
  format: 'compact_currency' | 'percentage' | 'number' | 'ratio';
}

/** A KPI is whatever the account declared it to be — impressions render as a count, CPM as money.
 * Formatting every KPI as a percentage turns 1.2M impressions into 120000000.00%. */
export function formatKpi(value: number | null, kpi: KpiDescriptor, symbol: string): string {
  if (value === null) return '—';
  switch (kpi.format) {
    case 'compact_currency':
      return compactCurrency(value, symbol);
    case 'percentage':
      return percent(value);
    case 'ratio':
      return value.toFixed(2);
    default:
      return count(value);
  }
}

export type Sentiment = 'good' | 'bad' | 'neutral';

export function sentimentOf(value: number | null): Sentiment {
  if (value === null || value === 0) return 'neutral';
  return value > 0 ? 'good' : 'bad';
}
